import React, { useEffect, useState } from 'react';
import { SeekerHeader } from '@ui/components/SeekerHeader';
import { CTAGroup } from '../ui/OnboardingCTA';

type OnboardingCheckpointStepProps = {
  stepIndex: number;
  totalSteps: number;
  progressLabel: string;
  title: string;
  subtitle?: string;
  detailHeading?: string;
  detailBody?: string;
  nextHeading?: string;
  nextBody?: string;
  guideCountPostcode?: string;
  onBack: () => void;
  onContinue: () => void;
};

export function OnboardingCheckpointStep({
  stepIndex,
  totalSteps,
  progressLabel,
  title,
  subtitle,
  detailHeading,
  detailBody,
  nextHeading,
  nextBody,
  guideCountPostcode,
  onBack,
  onContinue
}: OnboardingCheckpointStepProps) {
  const progress = totalSteps > 0 ? ((stepIndex + 1) / totalSteps) * 100 : 0;
  const [guideCount, setGuideCount] = useState<number | null>(null);
  const [isGuideCountLoading, setIsGuideCountLoading] = useState(false);
  const [guideCountError, setGuideCountError] = useState(false);
  const needsGuideCount =
    (detailHeading?.includes('{guideCount}') ?? false) ||
    (detailBody?.includes('{guideCount}') ?? false);

  useEffect(() => {
    if (!needsGuideCount) {
      return;
    }

    let isActive = true;

    const fetchGuideCount = async () => {
      setIsGuideCountLoading(true);
      setGuideCountError(false);

      try {
        const total = Math.floor(Math.random() * (30 - 15 + 1)) + 15;
        setGuideCount(total);
      } catch (err) {
        console.error('Failed to fetch guide count', err);
        if (isActive) {
          setGuideCount(null);
          setGuideCountError(true);
        }
      } finally {
        if (isActive) {
          setIsGuideCountLoading(false);
        }
      }
    };

    fetchGuideCount();

    return () => {
      isActive = false;
    };
  }, [guideCountPostcode, needsGuideCount]);

  const placeholderValue = isGuideCountLoading
    ? '...'
    : guideCount !== null
    ? guideCount.toString()
    : guideCountError
    ? '0'
    : '0';

  const resolvedDetailHeading = detailHeading
    ? detailHeading.replace('{guideCount}', placeholderValue)
    : undefined;
  const resolvedDetailBody = detailBody
    ? detailBody.replace('{guideCount}', placeholderValue)
    : undefined;

  return (
    <div className="flex min-h-screen w-full flex-col lg:overflow-auto overflow-hidden">
      <div className="relative flex flex-1 w-full flex-col bg-[#fefefe]">
        {/* Mobile header */}
        <header className="flex lg:hidden h-16 shrink-0 items-center border-b border-onboarding-bg/25 bg-onboarding-header px-4">
          <div className="w-10" aria-hidden />
          <div className="flex flex-1 justify-center">
            <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
          </div>
          <div className="w-10" aria-hidden />
        </header>

        {/* Desktop header */}
        <div className="hidden lg:block">
          <SeekerHeader />
        </div>

        {/* Content wrapper */}
        <div className="flex flex-1 flex-col lg:items-center lg:px-[72px] lg:py-[40px] lg:gap-[40px]">
          {/* Progress bar */}
          <section className="flex shrink-0 flex-col gap-[6px] px-4 lg:px-0 pb-2 pt-4 lg:pt-0 lg:pb-0 w-full lg:max-w-[1296px]">
            <span className="text-[14px] leading-[1.6] text-onboarding-primary">{progressLabel}</span>
            <div className="h-2 w-full overflow-hidden rounded-full bg-[#C9C9C9]">
              <div
                className="h-full rounded-full bg-onboarding-accent transition-[width] duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </section>

          {/* Main content area */}
          <main className="flex flex-1 lg:flex-initial min-h-0 flex-col items-center gap-[24px] overflow-y-auto lg:overflow-visible px-4 lg:px-0 pb-10 lg:pb-[40px] pt-6 lg:pt-0 w-full lg:w-[509px]">
            {/* Progress image */}
            <div className="w-full h-[256px] rounded-[12px] overflow-hidden">
              <img
                src="/progress.webp"
                alt="Progress illustration"
                className="w-full h-full object-cover object-center"
              />
            </div>

            {/* Title */}
            <h2 className="font-recoleta text-[32px] text-[#214d3c] tracking-[-0.448px] leading-[44px] w-full">
              {title}
            </h2>

            {/* Detail card with border */}
            {(resolvedDetailHeading || resolvedDetailBody) && (
              <div className="border-2 border-[#214d3c] rounded-[16px] p-[24px] w-full">
                <div className="flex flex-col gap-[16px]">
                  <div className="flex flex-col gap-[8px]">
                    {resolvedDetailHeading && (
                      <h3 className="font-recoleta font-bold text-[24px] text-[#214d3c] leading-[28px]">
                        {resolvedDetailHeading}
                      </h3>
                    )}
                    {resolvedDetailBody && (
                      <div className="flex flex-col gap-[8px]">
                        {resolvedDetailBody.split('\n\n').map((paragraph, index) => (
                          <p key={index} className="text-[14px] text-black leading-[1.6]">
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="py-4 w-full">
              <CTAGroup
                onBack={onBack}
                onContinue={onContinue}
                className="w-full"
              />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
